import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContentText,
  DialogTitle,
  lighten,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  ArrowForward as ArrowForwardIcon,
  Cancel as CancelIcon,
  CheckBox as CheckBoxIcon,
  CheckCircle as CheckCircleIcon,
  SubdirectoryArrowRight as SubdirectoryArrowRightIcon
} from '@material-ui/icons';
import { State, StateEvent, StateTransition } from '@sierralabs/workflow';

import { FetchError } from '../../../common/api/api';
import { getEntityPrefix } from '../../../common/entityId.hooks';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { UserContext } from '../../../user/User.context';
import { useJiraConnectContext } from '../../jira-connect/JiraConnect.context';
import { CreateJiraIssuePlugin } from '../../workflow/plugins/services/CreateJiraIssuePlugin';
import { CreateSubTasksPlugin } from '../../workflow/plugins/services/CreateSubTasksPlugin';
import { TransitionJiraIssuePlugin } from '../../workflow/plugins/services/TransitionJiraIssuePlugin';
import { UpdateJiraIssuePlugin } from '../../workflow/plugins/services/UpdateJiraIssuePlugin';
import { CheckDependenciesPlugin } from '../../workflow/plugins/transition-conditions/CheckDependenciesPlugin';
import { ConfirmPlugin } from '../../workflow/plugins/transition-conditions/ConfirmPlugin';
import { RequiredFieldsPlugin } from '../../workflow/plugins/transition-conditions/RequiredFieldsPlugin';
import { SignaturePlugin } from '../../workflow/plugins/transition-conditions/SignaturePlugin';
import { useWorkflowContext } from '../../workflow/Workflow.context';
import { parseTaskStatus } from '../task.helper';
import { updateTask } from '../Task.hooks';
import { Task, TaskBulkEvent, TaskEntity } from '../Task.interface';
import { TaskContext, useGetWorkflowMachineState } from '../Task.machine';
import { findTaskJiraIssues } from '../TaskJira.hooks';

const StyledDialog = styled(Dialog)`
  .MuiLinearProgress-root {
    position: absolute;
    width: 100%;
  }
  .MuiTableCell-stickyHeader {
    background-color: ${props => props.theme.palette.background.paper};
  }

  .icon {
    vertical-align: middle;
  }
  .success-icon {
    color: ${props => props.theme.palette.success.main};
  }
  .error-icon {
    color: ${props => props.theme.palette.error.main};
  }

  .completed-row {
    background-color: ${props =>
      lighten(props.theme.palette.success.main, 0.85)};
    .status-icon {
      color: ${props => props.theme.palette.success.main};
    }
  }
  .error-row {
    background-color: ${props => lighten(props.theme.palette.error.main, 0.85)};
    .status-icon {
      color: ${props => props.theme.palette.error.main};
    }
  }
  .warning-row {
    background-color: ${props =>
      lighten(props.theme.palette.warning.main, 0.85)};
    .status-icon {
      color: ${props => props.theme.palette.warning.main};
    }
  }

  .display-transition {
    display: flex;
    align-items: center;
    column-gap: 2px;
  }

  .MuiDialogActions-root {
    display: block;
    white-space: nowrap;
    overflow-x: auto;
  }
  .transition-button .MuiButton-label {
    column-gap: 2px;
    .MuiSvgIcon-root {
      font-size: 1rem;
    }
  }
`;

interface SubTaskBulkEvent {
  path: string;
  event: StateEvent<TaskContext>;
  tasks: Task[];
}

interface TaskBulkItem {
  /** Auto-generated ID. */
  id: string;
  /** Main Task or SubTask. */
  task: Task;
  /** Parent task if {@linkcode task} is a SubTask. */
  parent?: Task;
  event?: StateEvent<TaskContext>;
  /** The Task entity. Always contains the main Task. */
  entity: TaskEntity;
  canTransition: boolean;
  error?: FetchError;
  selected?: boolean;
  hideTargetStatus?: boolean;
  status?: 'loading' | 'warn' | 'error' | 'done';
}

export interface TaskBulkActionDialogProps {
  open: boolean;
  bulkEvent: TaskBulkEvent;
  useDeprecatedTaskKey?: boolean;
  getTitle: (taskEntity: TaskEntity) => React.ReactNode;
  onClose?: (processedEntities?: TaskEntity[]) => void;
}

const TaskBulkActionDialog: React.FC<TaskBulkActionDialogProps> = props => {
  const { open, bulkEvent, useDeprecatedTaskKey, getTitle, onClose } = props;
  const { user } = useContext(UserContext);
  const didInit = useRef(false);
  const [isInitializing, setIsInitializing] = useState(true); // loading by default
  const [state, setState] = useState<{
    isLoading: boolean;
    didUpdate: boolean;
    event?: StateEvent<TaskContext>;
  }>({ isLoading: false, didUpdate: false });
  const [taskItems, setTaskItems] = useState<TaskBulkItem[]>([]);
  const [subTaskEvents, setSubTaskEvents] = useState<SubTaskBulkEvent[]>([]);
  const { render, runMachine, canTransition, handleTransition } =
    useGetWorkflowMachineState();
  const { isAvailable } = useJiraConnectContext();
  // since workflows are loaded asynchronously,
  // make sure to reinitialize items once loaded
  const { workflows } = useWorkflowContext();
  const isLoading = isInitializing || state.isLoading;

  const computedItems = useMemo(() => {
    const eligibleTaskItems: TaskBulkItem[] = [];
    const eligibleSelectedTaskItems: TaskBulkItem[] = [];
    const selectedMainTaskItems: TaskBulkItem[] = [];

    // use 1 loop for multiple filters
    for (const item of taskItems) {
      // eligible
      if (item.status !== 'done' && item.canTransition) {
        eligibleTaskItems.push(item);
        // eligible selected
        if (item.selected) {
          eligibleSelectedTaskItems.push(item);
          // eligible selected (bulk event stateNode path)
          if (item.task.status === bulkEvent.stateNode.path) {
            selectedMainTaskItems.push(item);
          }
        }
      }
    }

    const hasItems = eligibleTaskItems.length > 0;
    const allSelected =
      eligibleSelectedTaskItems.length >= eligibleTaskItems.length;
    const partialSelected =
      eligibleSelectedTaskItems.length > 0 &&
      eligibleSelectedTaskItems.length < eligibleTaskItems.length;

    return {
      eligibleTaskItems,
      eligibleSelectedTaskItems,
      selectedMainTaskItems,
      hasItems,
      allSelected,
      partialSelected
    };
  }, [bulkEvent.stateNode.path, taskItems]);

  const { selectedMainTaskItems, hasItems, allSelected, partialSelected } =
    computedItems;

  const isDisabled = (taskItem: TaskBulkItem | undefined) => {
    return (
      !taskItem ||
      isInitializing ||
      state.isLoading ||
      !taskItem.canTransition ||
      (['done', 'loading'] as TaskBulkItem['status'][]).includes(
        taskItem.status
      )
    );
  };

  const updateItem = (
    idOrPredicate:
      | string
      | ((value: TaskBulkItem, index: number, obj: TaskBulkItem[]) => unknown),
    updates: Partial<TaskBulkItem>
  ) => {
    setTaskItems(taskItems => {
      // match by id or provided function
      const index = taskItems.findIndex(
        typeof idOrPredicate === 'function'
          ? idOrPredicate
          : item => item.id === idOrPredicate
      );
      if (index > -1) {
        taskItems = Array.from(taskItems);
        const item = taskItems[index];
        taskItems[index] = { ...item, ...updates };
      }
      return taskItems;
    });
  };

  const bulkTransition = async (
    items: TaskBulkItem[],
    stateEvent: StateEvent<TaskContext>
  ) => {
    setState(state => ({ ...state, isLoading: true, event: stateEvent }));

    // list of plugins to skip after the first item transition
    const skip = {
      transitionConditions: [
        CheckDependenciesPlugin.type,
        ConfirmPlugin.type,
        RequiredFieldsPlugin.type,
        SignaturePlugin.type
      ],
      services: [CreateSubTasksPlugin.src],
      jiraPlugins: [
        CreateJiraIssuePlugin.src,
        UpdateJiraIssuePlugin.src,
        TransitionJiraIssuePlugin.src
      ]
    };
    // skip jira plugins if not within jira instance
    if (!isAvailable) {
      skip.services.push(...skip.jiraPlugins);
    }

    // reuse the first state for the next entries
    let firstState: State<TaskContext> | undefined;
    let { didUpdate } = state;
    for (const item of items) {
      // set loading state for current item
      updateItem(item.id, { status: 'loading', error: undefined });

      // set only if not the first state
      const skipServicePlugins = firstState ? skip.services : undefined;
      const skipTransitionConditionPlugins = firstState
        ? skip.transitionConditions
        : undefined;
      const autoSkipJiraPlugins = firstState ? skip.jiraPlugins : undefined;

      const { stateNode } = await runMachine({
        task: item.task,
        taskEntity: item.entity,
        user: user!,
        skipServicePlugins,
        skipTransitionConditionPlugins,
        autoSkipJiraPlugins
      });
      const event = stateNode?.events?.find(e => e.name === stateEvent.name);
      if (!stateNode || !event) {
        updateItem(item.id, {
          status: 'error',
          error: {
            statusCode: 0,
            error: 'Run Machine Error',
            message: !stateNode
              ? 'Could not determine the current state'
              : 'Workflow event not found'
          }
        });
        break;
      }

      const previousStatePath = stateNode.path;
      let newState: State<TaskContext> | undefined;
      try {
        newState = await handleTransition({
          event,
          task: item.task,
          taskEntity: item.entity,
          user: user!,
          skipServicePlugins,
          skipTransitionConditionPlugins,
          autoSkipJiraPlugins
        });
        console.debug(
          '[TaskBulkActionDialog] previousStatePath: %o newState: %o',
          previousStatePath,
          newState
        );
      } catch {
        // ignore transition errors and treat as cancelled
      }
      if (!newState || newState.path === previousStatePath) {
        // if cancelled, stop transitioning other items
        updateItem(item.id, {
          status: 'warn',
          error: {
            statusCode: 0,
            error: 'Transition Error',
            message: 'Transition failed or was cancelled.'
          }
        });
        break;
      }

      // set first state to be reused for next entries
      firstState = firstState || newState;

      // update task and set it to taskItem
      const { result } = updateTask(item.task.id || 0, {
        status: newState.path,
        event: event.toJson(),
        // use data from the first state since some of
        // the plugins for the next states are skipped
        reason: firstState.context.reason,
        subTaskUserIds: firstState.context.subTaskUserIds,
        pluginData: firstState.context.pluginData
      });
      const { data: updatedTask, error } = await result;
      if (!updatedTask || error) {
        updateItem(item.id, {
          status: 'error',
          error: error || {
            statusCode: 0,
            error: 'Update Error',
            message: 'Unable to update the Task item.'
          }
        });
        break;
      }

      // set didUpdate after successfully updating at least 1 task
      didUpdate = true;

      const previousStatus = item.task.status;
      if (updatedTask.id === item.task.id) {
        // directly update task properties
        // this would automatically apply to status to the parent task's subTask
        // as the subTask reference is the same
        Object.assign(item.task, updatedTask);
      }

      const didStatusChange = previousStatus !== item.task.status;

      setTaskItems(taskItems => {
        // filter related tasks and get index of item
        const updatedTaskItems: TaskBulkItem[] = [];
        for (let index = 0; index < taskItems.length; index++) {
          // find related task items that match the task but events can differ
          // e.g. same subTask for 2 items that have different events: approve and reject
          const taskItem = taskItems[index];

          // match exact event
          if (taskItem.id === item.id) {
            const updatedTaskItem: TaskBulkItem = { ...taskItem };
            // remove selection and mark as processed
            if (didStatusChange) {
              updatedTaskItem.selected = false;
              updatedTaskItem.status = 'done';
              updatedTaskItem.error = undefined;
              // hide the target status if status changed
              updatedTaskItem.hideTargetStatus = didStatusChange;
            } else {
              // handle cases where tasks do not transition (workflow guard checks failed)
              updatedTaskItem.status = 'warn';
              updatedTaskItem.error = {
                statusCode: 0,
                error: 'Transition Error',
                message: 'Transition checks failed.'
              };
            }
            updatedTaskItems.push(updatedTaskItem);
          } else if (
            // filter out related task items (other subTasks of different event)
            !(
              taskItem.task.id === item.task.id &&
              taskItem.entity.id === item.entity.id &&
              taskItem.entity.task &&
              item.entity.task &&
              taskItem.entity.task.id === item.entity.task.id
            ) &&
            // skip task items that are subTasks of the current task item
            // if they are not part of the updated task children array
            !(
              taskItem.parent &&
              taskItem.parent.id === item.task.id &&
              !(item.task.children || []).some(
                child => child.id === taskItem.task.id
              )
            )
          ) {
            updatedTaskItems.push(taskItem);
          }
        }
        return updatedTaskItems;
      });

      // run transitions for parent task when subTask is transitioned
      // with an updated status to properly run the actions for the parent task
      const { parent } = item;
      if (parent) {
        const matchParent = (item: TaskBulkItem) => item.task.id === parent.id;
        updateItem(matchParent, { status: 'loading', error: undefined });

        let parentItemStatus: TaskBulkItem['status'];
        const previousParentStatus = parent.status;
        const canParentTransition = Boolean(
          await canTransition(event, parent, item.entity, user!)
        );

        try {
          const { stateNode } = await runMachine({
            task: parent,
            taskEntity: item.entity,
            user: user!,
            skipServicePlugins,
            skipTransitionConditionPlugins,
            autoSkipJiraPlugins
          });

          // no need to update this task since parent tasks
          // are already updated by the subTask's updateTask() call
          const previousStatePath = stateNode?.path;
          const parentState = await handleTransition({
            event: undefined,
            task: parent,
            taskEntity: item.entity,
            user: user!,
            skipServicePlugins,
            skipTransitionConditionPlugins,
            autoSkipJiraPlugins
          });

          // mark as done if state path changed
          parentItemStatus =
            parentState && parentState.path !== previousStatePath
              ? 'done'
              : undefined;
        } catch {
          // ignore parent transition errors, treat as cancelled
          parentItemStatus = 'warn';
        }

        // directly update parent status and hide target status
        // only after parent task transition
        if (
          updatedTask.id === item.task.id &&
          updatedTask.parent?.status &&
          parent.status !== updatedTask.parent.status
        ) {
          parent.status = updatedTask.parent.status;
        }

        const didParentTransitionFail = parentItemStatus === 'warn';
        const didParentStatusChange = !!(
          parent &&
          previousParentStatus &&
          previousParentStatus !== parent.status
        );

        updateItem(matchParent, {
          status: parentItemStatus,
          canTransition: parentItemStatus === 'done' && canParentTransition,
          // hide target status for parent task item
          hideTargetStatus: didParentStatusChange,
          error: didParentTransitionFail
            ? {
                statusCode: 0,
                error: 'Transition Error',
                message: 'Transition failed or was cancelled.'
              }
            : undefined
        });

        // stop processing next item if cancelled
        if (didParentTransitionFail) {
          break;
        }
      }
    }

    setState({ isLoading: false, didUpdate, event: undefined });
  };

  const handleClose = () => {
    // close only when not processing items
    if (state.isLoading) {
      return;
    }
    // make sure entities are unique as task items can
    // reference the same task entities but with different events
    const entities = new Set<TaskEntity>();
    for (const item of taskItems) {
      item.status === 'done' && entities.add(item.entity);
    }
    // pass undefined if no processed entities
    onClose?.(entities.size > 0 ? Array.from(entities) : undefined);
  };

  const renderRow = (options: {
    taskItem: TaskBulkItem;
    disabled: boolean;
    task?: Task;
    entity?: TaskEntity;
  }) => {
    const {
      disabled,
      taskItem,
      task = taskItem.task,
      entity = taskItem.entity
    } = options;
    const transition = taskItem.event?.transitions[0];
    const isSubTask = taskItem.parent;
    const isDone = taskItem.status === 'done';
    const className = isDone
      ? 'completed-row'
      : taskItem.status === 'warn'
      ? 'warning-row'
      : taskItem.status === 'error'
      ? 'error-row'
      : undefined;
    const eligible = taskItem.canTransition
      ? {
          icon: CheckCircleIcon,
          className: 'success-icon',
          title: 'This item can transition to the next status.'
        }
      : {
          icon: CancelIcon,
          className: 'error-icon',
          title: 'Workflow rules prevent this item from being transitioned.'
        };
    return (
      <TableRow
        hover={!disabled}
        className={className}
        selected={taskItem.status === 'loading'}
        onClick={() => {
          if (!disabled) {
            updateItem(taskItem.id, {
              error: undefined,
              status: undefined,
              selected: !taskItem.selected
            });
          }
        }}
      >
        <TableCell padding='checkbox'>
          {taskItem.status === 'loading' ? (
            <CircularProgress color='primary' size={20} className='icon' />
          ) : isDone || taskItem.error ? (
            <Tooltip
              arrow
              placement='top'
              title={isDone ? 'Done' : taskItem.error?.message || ''}
            >
              <CheckBoxIcon fontSize='small' className='icon status-icon' />
            </Tooltip>
          ) : (
            <Checkbox
              size='small'
              color='primary'
              disabled={disabled}
              checked={taskItem.selected || false}
            />
          )}
        </TableCell>
        <TableCell align='center'>
          {!isSubTask ? (
            // NOTE: id will be changed to use task.key in SLQMS-921
            useDeprecatedTaskKey ? (
              `${getEntityPrefix(entity.task!.type)}-${entity.id}`
            ) : (
              entity.task!.key
            )
          ) : (
            <SubdirectoryArrowRightIcon
              color='action'
              fontSize='small'
              className='icon'
            />
          )}
        </TableCell>
        <TableCell>
          {taskItem.event ? (
            taskItem.event.name
          ) : (
            <Typography variant='inherit' color='textSecondary'>
              N/A
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <div className='display-transition'>
            <TaskStatusLabel task={task} />
            {transition && !taskItem.hideTargetStatus && (
              <>
                <ArrowForwardIcon
                  color='action'
                  className='icon'
                  fontSize='small'
                />
                <TaskStatusLabel task={task} status={transition.target} />
              </>
            )}
          </div>
        </TableCell>
        <TableCell align='center'>
          <Tooltip arrow placement='top' title={eligible.title}>
            <eligible.icon
              fontSize='small'
              className={`icon ${eligible.className}`}
            />
          </Tooltip>
        </TableCell>
        <TableCell>
          {!isSubTask
            ? getTitle?.(entity)
            : task.user &&
              `Assigned: ${task.user.firstName} ${task.user.lastName}`}
        </TableCell>
      </TableRow>
    );
  };

  const renderSubTasks = (task: Task | undefined) => {
    return task?.children?.map(subTask => {
      return taskItems.map((taskItem, index) => {
        // filter inside map to keep track of index
        return taskItem.task.id !== subTask.id ? null : (
          <Fragment key={`task-${taskItem.task.id}-${taskItem.id}-${index}`}>
            {renderRow({
              taskItem,
              task: subTask,
              disabled: isDisabled(taskItem)
            })}
            {renderSubTasks(subTask)}
          </Fragment>
        );
      });
    });
  };

  const renderButton = (options: {
    key?: React.Key;
    status: string;
    event: StateEvent<TaskContext>;
    selectedItems: TaskBulkItem[];
  }) => {
    const { status, event, selectedItems } = options;
    const parsedStatus = parseTaskStatus(status);
    const transition = event.transitions[0] as StateTransition | undefined;
    const parsedTargetStatus = transition && parseTaskStatus(transition.target);
    return (
      <Button
        color='primary'
        variant='contained'
        className='transition-button'
        key={options.key}
        disabled={state.isLoading || selectedItems.length === 0}
        endIcon={
          state.isLoading &&
          state.event &&
          state.event.id === event.id && <CircularProgress size={16} />
        }
        onClick={() => bulkTransition(selectedItems, event)}
      >
        {/* only display target status if not the same as source status */}
        {parsedStatus}
        {parsedTargetStatus && parsedStatus !== parsedTargetStatus && (
          <>
            <ArrowForwardIcon fontSize='small' className='icon' />
            {parsedTargetStatus}
          </>
        )}
        {selectedItems.length > 0 && ` (${selectedItems.length})`}
      </Button>
    );
  };

  // allow resetting state
  useEffect(() => {
    if (!open) {
      didInit.current = false;
    }
  }, [open]);

  // allow reinit (next effect) if workflows are updated
  useEffect(() => {
    didInit.current = false;
  }, [workflows]);

  useEffect(() => {
    // skip setting state if already loaded
    // to avoid triggering the effect on save during development
    if (didInit.current) {
      return;
    }
    didInit.current = true;

    const taskItems: TaskBulkItem[] = [];
    const subTaskEvents: SubTaskBulkEvent[] = [];

    // get all user assigned SubTasks recursively
    const recursiveProcessSubTasks = async (task: Task, entity: TaskEntity) => {
      for (const subTask of task.children || []) {
        const { stateNode } = await runMachine({ task: subTask });
        const events = stateNode?.events || [];
        if (events.length === 0) {
          // for subTasks with no events, display the status
          taskItems.push({
            id: uuid(),
            task: subTask,
            parent: task,
            entity,
            canTransition: false
          });
        }
        for (const event of events) {
          taskItems.push({
            id: uuid(),
            task: subTask,
            parent: task,
            event,
            entity,
            // for subTasks, allow transition only if assigned to the current user
            canTransition: !!(
              subTask.user &&
              subTask.user.id === user?.id &&
              (await canTransition(event, subTask, entity, user!))
            )
          });

          const subTaskEvent = subTaskEvents.find(
            e => e.event.id === event.id && e.path === subTask.status
          );
          if (subTaskEvent) {
            subTaskEvent.tasks.push(subTask);
          } else {
            subTaskEvents.push({
              path: subTask.status,
              event,
              tasks: [subTask]
            });
          }
        }

        await recursiveProcessSubTasks(subTask, entity);
      }
    };

    (async () => {
      setIsInitializing(true);

      for (const entity of bulkEvent.taskEntities) {
        const { task } = entity;
        if (!task) continue;

        // NOTE: fallback only. product board query should include task.jiraIssues
        // load task data if not already in task object as they may
        // be needed by the workflow plugins (e.g. task.jiraIssues)
        if (!task.jiraIssues) {
          // load and directly assign task jira issues to task object to persist it
          const { result } = findTaskJiraIssues(task.id || 0);
          const { data } = await result;
          if (data) {
            task.jiraIssues = data;
          }
        }

        const { stateNode } = await runMachine({ task });
        const event = stateNode?.events?.find(
          event => event.name === bulkEvent.event.name
        );
        if (!event) continue;
        taskItems.push({
          id: uuid(),
          task,
          event,
          entity,
          canTransition: Boolean(
            await canTransition(event, task, entity, user!)
          )
        });
        await recursiveProcessSubTasks(task, entity);
      }

      setIsInitializing(false);
      setTaskItems(taskItems);
      setSubTaskEvents(subTaskEvents);
    })();
  }, [bulkEvent, user, workflows, canTransition, runMachine]);

  return (
    <StyledDialog open={open} fullWidth maxWidth='lg' onClose={handleClose}>
      {isLoading && <LinearProgress />}
      <DialogTitle>Bulk Action</DialogTitle>

      <Box mx={3}>
        <DialogContentText>
          Select from the list of items to perform the bulk action.
        </DialogContentText>
      </Box>

      <TableContainer>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox'>
                <Checkbox
                  size='small'
                  color='primary'
                  disabled={state.isLoading}
                  indeterminate={partialSelected}
                  checked={hasItems && allSelected}
                  onChange={event => {
                    setTaskItems(taskItems => {
                      return taskItems.map(item => {
                        if (item.status !== 'done' && item.canTransition) {
                          item = {
                            ...item,
                            error: undefined,
                            status: undefined,
                            selected: event.target.checked
                          };
                        }
                        return item;
                      });
                    });
                  }}
                />
              </TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Transition</TableCell>
              <TableCell>Eligible</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {taskItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  {isLoading ? 'Loading items...' : 'No items to transition.'}
                </TableCell>
              </TableRow>
            )}
            {bulkEvent.taskEntities.map((entity, index) => {
              const taskItemIndex = taskItems.findIndex(
                s => s.task.id === entity.task?.id
              );
              const taskItem =
                taskItemIndex > -1 ? taskItems[taskItemIndex] : undefined;
              return !taskItem ? null : (
                <Fragment key={`item-${taskItem.id}-${index}`}>
                  {renderRow({
                    entity,
                    taskItem,
                    task: entity.task,
                    disabled: isDisabled(taskItem)
                  })}
                  {renderSubTasks(entity.task)}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <DialogActions>
        <Button
          variant='outlined'
          disabled={state.isLoading}
          onClick={handleClose}
        >
          {state.didUpdate ? 'Close' : 'Cancel'}
        </Button>

        {subTaskEvents.map((subTaskEvent, index) => {
          const { event } = subTaskEvent;
          const selectedItems = taskItems.filter(item => {
            return (
              item.selected &&
              item.task.status === subTaskEvent.path &&
              item.event?.id === event.id
            );
          });
          return renderButton({
            key: `${subTaskEvent.path}-${event.id}-${index}`,
            event,
            status: subTaskEvent.path,
            selectedItems
          });
        })}

        {renderButton({
          event: bulkEvent.event,
          status: bulkEvent.stateNode.path,
          selectedItems: selectedMainTaskItems
        })}
      </DialogActions>

      {/* render any workflow steps during transition */}
      {render}
    </StyledDialog>
  );
};

export default TaskBulkActionDialog;
